import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsWalk as WalkIcon,
  LocalFireDepartment as FireIcon,
  Restaurant as RestaurantIcon,
  HealthAndSafety as HealthIcon
} from '@mui/icons-material';
import { Product } from '../types';

interface ProductDetailsProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ open, onClose, product }) => {
  if (!product) return null;

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'success';
    if (score >= 40) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excelent';
    if (score >= 70) return 'Bun';
    if (score >= 40) return 'Moderat';
    return 'Sărac';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="div">
          {product.name}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Product Basic Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informații Produs
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Cod de Bare
                  </Typography>
                  <Typography variant="body1">
                    {product.barcode}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Creat
                  </Typography>
                  <Typography variant="body1">
                    {new Date(product.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
              {product.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Descriere
                  </Typography>
                  <Typography variant="body1">
                    {product.description}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Health Analysis */}
          {product.health_analysis && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <HealthIcon sx={{ mr: 1 }} />
                  Analiza Sănătății
                </Typography>

                {/* Health Score */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h2" color={`${getScoreColor(product.health_analysis.health_score)}.main`} sx={{ mb: 1 }}>
                    {product.health_analysis.health_score.toFixed(0)}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Scorul de Sănătate
                  </Typography>
                  <Chip 
                    label={getScoreLabel(product.health_analysis.health_score)} 
                    color={getScoreColor(product.health_analysis.health_score)}
                    size="medium"
                    sx={{ fontSize: '1rem', py: 2, px: 3 }}
                  />
                </Box>

                {/* Walking Distance */}
                <Alert 
                  icon={<WalkIcon />} 
                  severity="info" 
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body1">
                    <strong>Distanța de mers recomandată:</strong> {product.health_analysis.walking_distance_km.toFixed(1)} km
                  </Typography>
                  <Typography variant="body2">
                    Aceasta este distanța aproximativă pe care ar trebui să o parcurgi pentru a arde caloriile din 100g/100ml din acest produs.
                  </Typography>
                </Alert>

                {/* Overall Assessment */}
                <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Evaluare Generală
                </Typography>
                  <Typography variant="body1">
                    {product.health_analysis.analysis_details.overall_assessment}
                  </Typography>
                </Box>

                {/* Positive and Negative Aspects */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle1" gutterBottom color="success.main">
                      Aspecte Pozitive
                    </Typography>
                    <List dense>
                      {product.health_analysis.analysis_details.positive_aspects.map((aspect, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={aspect}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle1" gutterBottom color="error.main">
                      Aspecte Negative
                    </Typography>
                    <List dense>
                      {product.health_analysis.analysis_details.negative_aspects.map((aspect, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <WarningIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={aspect}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>

                {/* Dangerous Ingredients */}
                {product.health_analysis.dangerous_ingredients.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom color="error.main">
                      Ingrediente Periculoase
                    </Typography>
                    {product.health_analysis.dangerous_ingredients.map((ingredient, index) => (
                      <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">
                          {ingredient.name} (Nivel de Pericol: {ingredient.danger_level}/10)
                        </Typography>
                        <Typography variant="body2">
                          {ingredient.reason}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Impact asupra Sănătății:</strong> {ingredient.health_impact}
                        </Typography>
                      </Alert>
                    ))}
                  </Box>
                )}

                {/* Recommendations */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Recomandări
                  </Typography>
                  <List dense>
                    {product.health_analysis.analysis_details.recommendations.map((recommendation, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <RestaurantIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={recommendation} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Ingredients */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ingrediente
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {product.ingredients.map((ingredient, index) => (
                  <Chip 
                    key={index} 
                    label={ingredient} 
                    variant="outlined" 
                    size="small"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Nutritional Information */}
          {product.nutritional_declaration && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informații Nutriționale (per 100g)
                </Typography>
                <Grid container spacing={2}>
                  {product.nutritional_declaration.calories !== null && product.nutritional_declaration.calories !== undefined && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary.main">
                          {product.nutritional_declaration.calories}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Calorii
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {product.nutritional_declaration.protein !== null && product.nutritional_declaration.protein !== undefined && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="success.main">
                          {product.nutritional_declaration.protein}g
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Proteine
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {product.nutritional_declaration.carbohydrates !== null && product.nutritional_declaration.carbohydrates !== undefined && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="warning.main">
                          {product.nutritional_declaration.carbohydrates}g
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Carbohidrați
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {product.nutritional_declaration.fat !== null && product.nutritional_declaration.fat !== undefined && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="error.main">
                          {product.nutritional_declaration.fat}g
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Grăsimi
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Închide
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetails;
